import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  ButtonBase,
  Collapse,
  Fade,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';

import { IQuestion } from '../../../../store/QuestionSlice';
import {
  ISubmission,
  ISubmissionQuestion,
  getSubmissionState,
  updateSubmissionQuestionOpenAiFeedback,
} from '../../../../store/SubmissionSlice';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import MarkdownRenderer from '../../../../helper/MarkdownRenderer/MarkdownRenderer';
import BasicLoader from '../../../../components/Loaders/BasicLoader';

type TMarkingSchemeViewerProps = {
  questionData: IQuestion;
  selectedQuestion: ISubmissionQuestion;
  submissionData: ISubmission;
};

const boxStyles: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  position: 'relative',
  borderRadius: '10px',
  overflow: 'hidden',
  p: 2,
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '10px',
    padding: '2px',
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    zIndex: -1,
  },
  background:
    'linear-gradient(90deg, rgba(219, 134, 248, 0.15) 0%, rgba(11, 196, 255, 0.15) 100%)',
};

const DetailedMarkingSchemeViewer: React.FC<TMarkingSchemeViewerProps> = ({
  questionData,
  selectedQuestion,
  submissionData,
}) => {
  const dispatch = useDispatch();

  // State
  const [isVisible, setIsVisible] = useState(true);

  // Store
  const { status, questionAIExplanation, aiExplanationStatus } = useAppSelector(
    getSubmissionState
  );

  const handleFeedback = (feedback: 'like' | 'unlike') => {
    if (
      questionData &&
      submissionData &&
      submissionData.questions &&
      submissionData.questions.length
    ) {
      const submissionQuestionData = submissionData.questions.find(
        (submissionQuestion) => submissionQuestion.question === questionData._id
      );

      let openAiFeedback: -1 | 0 | 1 = 0;

      if (feedback === 'like') {
        openAiFeedback = 1;
      } else if (feedback === 'unlike') {
        openAiFeedback = -1;
      }

      if (
        submissionQuestionData?.openAiFeedback !== undefined &&
        openAiFeedback === submissionQuestionData.openAiFeedback
      ) {
        openAiFeedback = 0;
      }

      dispatch(
        updateSubmissionQuestionOpenAiFeedback({
          questionId: questionData._id,
          submissionId: submissionData._id,
          feedback: openAiFeedback,
        })
      );
    }
  };

  return (
    <Fade in>
      <div>
        <Box sx={boxStyles}>
          <Stack direction="row" alignItems="center" gap="12px">
            <img
              alt="star"
              src={`${process.env.PUBLIC_URL}/images/shapes/detailed-feedback-start.png`}
              style={{ width: '28px', height: '24px' }}
            />
            <Typography color="#12AAFF">Detailed Marking Scheme</Typography>
          </Stack>
          <ButtonBase
            onClick={() => setIsVisible((prevState) => !prevState)}
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: TP_COLORS.gray.darken,
            }}
          >
            {isVisible ? 'Hide' : 'Show'}
          </ButtonBase>
        </Box>

        <Collapse in={isVisible}>
          <Box style={{ padding: '16px 32px' }}>
            {aiExplanationStatus === 'loading' ? (
              <BasicLoader
                containerProps={{
                  minHeight: '200px',
                  alignItems: 'center',
                }}
              />
            ) : null}
            {questionAIExplanation ? (
              <>
                <MarkdownRenderer content={questionAIExplanation} />
                <Stack direction="row">
                  <ButtonBase
                    color="info"
                    onClick={() => handleFeedback('like')}
                    disabled={status === 'loading'}
                    sx={{
                      p: '5px',
                      borderRadius: '4px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'info.main',
                      marginRight: '10px',
                      backgroundColor: 'light',
                    }}
                  >
                    {selectedQuestion.openAiFeedback === 1 ? (
                      <ThumbUpIcon color="info" />
                    ) : (
                      <ThumbUpOffAltIcon color="info" />
                    )}
                  </ButtonBase>
                  <ButtonBase
                    color="info"
                    disabled={status === 'loading'}
                    onClick={() => handleFeedback('unlike')}
                    sx={{
                      p: '5px',
                      borderRadius: '4px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'info.main',
                      backgroundColor: 'light',
                    }}
                  >
                    {selectedQuestion.openAiFeedback === -1 ? (
                      <ThumbDownIcon color="info" />
                    ) : (
                      <ThumbDownOffAltIcon color="info" />
                    )}
                  </ButtonBase>
                </Stack>
              </>
            ) : null}
          </Box>
        </Collapse>
      </div>
    </Fade>
  );
};

export default DetailedMarkingSchemeViewer;
